'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetPublicPartnerCreator } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { Agreement01IconSolid, ArrowUpRight01Icon } from '@/components/ui/icon/glyphs';
import { IdentityPill } from '@/components/ui/identity-pill';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { Link } from '@/i18n/navigation';
import { logger } from '@/lib/telemetry/logger';
import { FeaturedLogomark, IdentityBeam } from './identity-marks';
import { CreatorIdentityAvatar, type MarketIdentityData } from './market-identity';
import { partnerSocialIcon, partnerSocialLabel } from './partner-social';

/**
 * Creator strip on proposal and market detail pages: who this market belongs
 * to, with the identity beam border. Influencer markets show the partner
 * creator (avatar, name, partner mark, bio, socials, "View profile");
 * Featured markets show Morfi Markets with the Featured pill.
 */
export function MarketIdentitySection(t0) {
  const $ = _c(6);
  const {
    identity
  } = t0;
  const enabled = useFeatureGate("creator_and_platform_markets");
  if (enabled !== true || !identity || !identity.marketType || identity.marketType === "ordinary") {
    return null;
  }
  const featured = identity.marketType === "featured";
  let t1;
  if ($[0] !== featured || $[1] !== identity) {
    t1 = <section data-testid="creator-strip" className="flex flex-col gap-3 rounded-card border border-border-primary bg-background-primary p-4 sm:flex-row sm:items-center sm:gap-3.5 sm:p-4.5">{featured ? <FeaturedStrip /> : <PartnerStrip identity={identity} />}</section>;
    $[0] = featured;
    $[1] = identity;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== identity.marketType || $[4] !== t1) {
    t2 = <IdentityBeam marketType={identity.marketType}>{t1}</IdentityBeam>;
    $[3] = identity.marketType;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function FeaturedStrip() {
  const $ = _c(26);
  const t = useTranslations("marketIdentity");
  let t0;
  if ($[0] !== t) {
    t0 = t("officialName");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <OptimizedImage src="/icons/morfi-logomark.svg" alt={t0} variant="avatar" preview={false} nsfw={false} size={48} rounded="rounded-control" className="shrink-0" />;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FeaturedLogomark />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("featured");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <IdentityPill tone="featured" leading={t2}>{t3}</IdentityPill>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("featuredDescription");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <p className="mt-1 text-caption text-foreground-secondary">{t5}</p>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <div className="min-w-0 flex-1">{t4}{t6}</div>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t1 || $[17] !== t7) {
    t8 = <div className="flex min-w-0 flex-1 items-center gap-3.5">{t1}{t7}</div>;
    $[16] = t1;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t) {
    t9 = t("viewOfficial");
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <StripAction href="/morfi-markets" label={t9} />;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <>{t8}{t10}</>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function PartnerStrip(t0) {
  const $ = _c(27);
  const {
    identity
  } = t0;
  const t = useTranslations("marketIdentity");
  const tPartner = useTranslations("profile.partner");
  const creator = identity.associatedCreator;
  const name = creator?.displayName ?? creator?.handle ?? creator?.walletAddress ?? "";
  const href = creator ? `/portfolio/${creator.walletAddress}` : null;
  let t1;
  if ($[0] !== identity) {
    t1 = <div className="shrink-0"><CreatorIdentityAvatar identity={identity} size="lg" /></div>;
    $[0] = identity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== href || $[3] !== name) {
    t2 = href ? <Link href={href} className="text-body font-medium text-foreground-primary">{name}</Link> : <span className="text-body font-medium text-foreground-primary">{name}</span>;
    $[2] = href;
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon icon={Agreement01IconSolid} size={12} />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== tPartner) {
    t4 = tPartner("badge");
    $[6] = tPartner;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <span className="inline-flex items-center gap-1 text-caption text-product-partner-content">{t3}{t4}</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <div className="flex flex-wrap items-center gap-2">{t2}{t5}</div>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== identity.partnerCreatorId) {
    t7 = identity.partnerCreatorId ? <PartnerDetails id={identity.partnerCreatorId} /> : null;
    $[13] = identity.partnerCreatorId;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <div className="min-w-0 flex-1">{t6}{t7}</div>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t1 || $[19] !== t8) {
    t9 = <div className="flex min-w-0 flex-1 items-start gap-3.5">{t1}{t8}</div>;
    $[18] = t1;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== href || $[22] !== t) {
    t10 = href ? <StripAction href={href} label={t("viewProfile")} /> : null;
    $[21] = href;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
function StripAction(t0) {
  const $ = _c(4);
  const {
    href,
    label
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={ArrowUpRight01Icon} size={12} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== href || $[2] !== label) {
    t2 = <Link href={href} className="inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-control border border-border-primary px-4 text-body font-medium text-foreground-primary transition-colors hover:bg-background-tertiary max-sm:w-full">{label}{t1}</Link>;
    $[1] = href;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function PartnerDetails(t0) {
  const $ = _c(30);
  const {
    id
  } = t0;
  const t = useTranslations("marketIdentity");
  let t1;
  if ($[0] !== id) {
    t1 = {
      id
    };
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      retry: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const query = useGetPublicPartnerCreator(t1, t2);
  const failed = query.error !== null;
  let t3;
  let t4;
  if ($[3] !== failed) {
    t3 = () => {
      if (failed) {
        logger.warn("market.identity_profile.load_failed", {
          stage: "public_profile"
        });
      }
    };
    t4 = [failed];
    $[3] = failed;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const profile = query.data;
  if (query.isPending) {
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Skeleton className="mt-2 h-9 w-full max-w-md" />;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  if (failed) {
    let t5;
    if ($[7] !== t) {
      t5 = t("loadError");
      $[7] = t;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== t5) {
      t6 = <span>{t5}</span>;
      $[9] = t5;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== query) {
      t7 = () => {
        query.refetch();
      };
      $[11] = query;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== t) {
      t8 = t("retry");
      $[13] = t;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== t7 || $[16] !== t8) {
      t9 = <AnimatedButton variant="secondary" onClick={t7}>{t8}</AnimatedButton>;
      $[15] = t7;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== t6 || $[19] !== t9) {
      t10 = <div role="alert" className="mt-1 flex flex-wrap items-center gap-3 text-caption">{t6}{t9}</div>;
      $[18] = t6;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    return t10;
  }
  if (!profile) {
    return null;
  }
  let t5;
  if ($[21] !== profile.bio) {
    t5 = profile.bio ? <p className="mt-1 break-words text-caption text-foreground-secondary">{profile.bio}</p> : null;
    $[21] = profile.bio;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== profile.socialLinks || $[24] !== profile.website || $[25] !== t) {
    t6 = profile.socialLinks.length > 0 || profile.website ? <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-caption text-foreground-primary">{profile.socialLinks.map(_temp)}{profile.website ? <a href={profile.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-foreground-secondary"><Icon icon={ArrowUpRight01Icon} size={14} />{t("website")}</a> : null}</div> : null;
    $[23] = profile.socialLinks;
    $[24] = profile.website;
    $[25] = t;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t5 || $[28] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
}
function _temp(social) {
  return <a key={social.platform} href={social.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-foreground-secondary"><Icon icon={partnerSocialIcon(social.platform)} size={14} />{partnerSocialLabel(social.platform)}</a>;
}